import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { ReactComponent as PersonWalkingIcon } from '../../../SVGAssets/person-walking.svg';
import useMapStateStore from '../../../stores/mapStateStore';
import { CONTINUE_TOUR_BUTTON_TEXT } from '../utils/helpers';
import tgai from '../TourGuideAi.module.scss';

const PRIMARY_ACTIONS = ['next_step', 'collect_interests', 'restart_conversation', 'continue'];

const MINOR_ACTIONS = ['flush_deferred', 'end_exploration'];

const Buttons = ({
  buttons,
  continueButton,
  links,
  conversationState,
  onButtonClick,
  isLatest,
}) => {
  const {
    selectedInterests, selectedAmenities, visitedAmenities, visitedExternalLocations,
    selectedFollowUpAction,
  } = conversationState;
  const amenityModalOpen = useMapStateStore((s) => s.amenityModalOpen);

  const allButtons = [...buttons,
    ...(continueButton ? [{ ...continueButton, action: continueButton.action || 'continue' }] : []),
    ...links];

  if (allButtons.length === 0) return null;

  const allInterestButtons = buttons.length > 0 && buttons.every(
    (button) => button.action === 'toggle_interest',
  );

  return (
    <div
      className={cx(tgai.stack, tgai.s, {
        [tgai.twoCol]: allInterestButtons,
      })}
    >
      {allButtons.map((button) => {
        const isPrimary = PRIMARY_ACTIONS.includes(button.action);
        const isMinor = MINOR_ACTIONS.includes(button.action) && buttons.length > 0;
        let isSelected = false;
        let isDisabled = false;

        if (!isLatest && button.action !== 'view_amenity' && button.action !== 'toggle_amenity' && button.action !== 'see_external_locations') {
          isDisabled = true;
        }

        if (button.action === 'toggle_interest') {
          isSelected = selectedInterests.some((i) => i.uuid === button.interest);
          if (!isDisabled) {
            isDisabled = selectedInterests.length >= 3 && !isSelected;
          }
        }

        if (button.action === 'toggle_amenity' || button.action === 'view_amenity') {
          isSelected = selectedAmenities?.includes(button.amenity);
        }

        if (
          button.action === 'ask_question'
          || button.action === 'continue_tour'
          || button.action === 'decline_more_places'
        ) {
          isSelected = selectedFollowUpAction === button.action;
          if (!isLatest && isSelected) {
            isDisabled = true;
          }
        }

        const isVisitedAmenity = (button.action === 'toggle_amenity' || button.action === 'view_amenity')
          && visitedAmenities?.includes(button.amenity)
          && !amenityModalOpen;

        const isVisitedExternal = button.action === 'see_external_locations'
          && visitedExternalLocations?.includes(button.text || 'external');

        const isVisited = isVisitedAmenity || isVisitedExternal;

        const buttonKey = button.interest || button.amenity || button.action || button.text;

        const buttonText = button.text === CONTINUE_TOUR_BUTTON_TEXT && !isMinor
          ? 'Continue Tour'
          : button.text;

        const buttonClass = cx({
          [tgai.btn]: !isMinor,
          [tgai.selected]: isSelected,
          [tgai.textBtn]: isMinor,
          [tgai.primary]: isPrimary && !isMinor,
          [tgai.secondary]: !isPrimary && !isMinor,
          [tgai.big]: button.interest,
          [tgai.long]: (buttonText && buttonText.length > 20),
          [tgai.visited]: isVisited,
        });

        return (
          <button
            type="button"
            key={buttonKey}
            className={buttonClass}
            onClick={() => onButtonClick(button)}
            disabled={isDisabled}
            data-tgai-button
            data-action={button.action}
            data-amenity={button.amenity}
            data-interest={button.interest}
            data-selected={isSelected || undefined}
          >
            {isVisited && <PersonWalkingIcon className={tgai.visitedIcon} />}
            {buttonText}
          </button>
        );
      })}
    </div>
  );
};

Buttons.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.string,
    interest: PropTypes.string,
    amenity: PropTypes.string,
  })),
  continueButton: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.string,
  }),
  links: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.string,
  })),
  conversationState: PropTypes.shape({
    selectedInterests: PropTypes.arrayOf(PropTypes.shape({
      uuid: PropTypes.string,
      label: PropTypes.string,
    })),
    selectedAmenities: PropTypes.arrayOf(PropTypes.string),
    visitedAmenities: PropTypes.arrayOf(PropTypes.string),
    visitedExternalLocations: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onButtonClick: PropTypes.func.isRequired,
  isLatest: PropTypes.bool,
};

Buttons.defaultProps = {
  buttons: [],
  continueButton: null,
  links: [],
  isLatest: true,
};

export default Buttons;
